import React from "react";
import { Modal } from "components/modal/Modal";
import axios from "api";
import { Spinner } from "components/Spinner/Spinner";
import { ApiResponse } from "@avamae/formbuilder/dist/FormBuilder";
import warningIcon from "../../../assets/Icon_Notification_Warning.png";

type FailedDeletion = {
  id: number;
  reason: string;
};

type DeletionResponse = {
  successfulDeletions: number[];
  failedDeletions: FailedDeletion[];
};

type ErrorDeletion = {
  hasError: boolean;
  failedDeletions: FailedDeletion[];
};

type Props<Data> = {
  open: boolean;
  close(): void;
  url: string;
  selectedData: (Data | undefined)[];
  selectedIds: React.ReactText[];
  idFromRowOption: React.ReactText | null;
  noun: string;
  pluralNoun?: string;
  stripInfo?: boolean;
  renderListItem(data: Data): string | React.ReactNode;
  additionalDataIDs?: any;
  parentSelectedIdName?: string;
  parentSelectedId?: number;
};

function DeleteModal<Data>(props: Props<Data>) {
  const [submitting, setSubmitting] = React.useState(false);
  const [errorState, setErrorState] = React.useState<ErrorDeletion | undefined>();

  const {
    open,
    close,
    noun,
    selectedIds,
    selectedData,
    idFromRowOption,
    renderListItem,
    additionalDataIDs,
    parentSelectedIdName,
    parentSelectedId,
  } = props;

  const pluralNoun = props.pluralNoun ?? noun + "s";

  const idsToDelete = idFromRowOption ? [idFromRowOption] : selectedIds;

  const handleDelete = () => {
    if (submitting) {
      return;
    }

    setSubmitting(true);
    let payload: any = { idsToDelete }; //document versions - additionalDataIDs

    if (parentSelectedIdName && parentSelectedId) {
      payload[parentSelectedIdName] = parentSelectedId;
    }
    axios
      .post<ApiResponse<any, DeletionResponse>>(props.url, payload)
      .then((res) => {
        if (res.status === 200) {
          if (res.data.status === "1" && res.data.details.failedDeletions.length === 0) {
            close();
          } else {
            // Failed deleting some users
            setErrorState({
              hasError: true,
              failedDeletions: res.data.details.failedDeletions,
            });
          }
        }
      })
      .catch((err) => {
        // Generic error
        setErrorState({
          hasError: true,
          failedDeletions: [],
        });
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <Modal
      open={open}
      closeModal={close}
      title={`Delete ${noun[0] + noun.slice(1)}`}
      actionButton={
        <button data-testid="delete-confirm" className="Btn BtnRed" onClick={handleDelete}>
          Delete
        </button>
      }
    >
      <div data-testid="delete-modal" className="DeleteModal">
        <h4>Permanently Delete</h4>

        <p>
          {`Are you sure you want to permanently delete ${
            idsToDelete.length > 1 ? `these ${pluralNoun}` : `this ${noun}`
          }?`}
        </p>
        {!props.stripInfo && (
          <>
            <p className="BoldText">
              {idsToDelete.length > 1 ? `These ${pluralNoun} ` : `This ${noun} `}
              and associated information will be permanently deleted
            </p>

            <ul>
              {selectedData.map((user, i) => (
                <div key={i}>{user && renderListItem(user)}</div>
              ))}
            </ul>
          </>
        )}
        <div className="DeleteModalWarning">
          <img src={warningIcon} alt="warning" />

          <p>This action cannot be undone</p>
        </div>

        {errorState?.hasError && (
          <div className="DeleteModalWarningDetails">
            {errorState.failedDeletions.length > 1
              ? `Error deleting the following ${pluralNoun}:`
              : `Error deleting the following ${noun}:`}
            <ul>
              {errorState.failedDeletions.length === 0
                ? idsToDelete.map((id) => <li key={id}>{id}</li>)
                : errorState.failedDeletions.map((d, i) => (
                    <li key={i}>
                      {d.id}: {d.reason}
                    </li>
                  ))}
            </ul>
          </div>
        )}
        {submitting && <Spinner positionAbsolute />}
      </div>
    </Modal>
  );
}

export default DeleteModal;
